import { Component, computed, input, signal } from "@angular/core";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../../core/utils";
import { Constants } from "../../../../constants/constants";
import type { EntityPerformanceModelType } from "../../../../models";

type RankedEntity = {
  id: number;
  rank: number;
  name: string;
  score: number;
  percentage: number;
  color: string;
  valueText: string;
};

@Component({
  selector: "adaa-entity-performance-ranking",
  imports: [TranslatePipe, NgbTooltip],
  templateUrl: "./entity-performance-ranking.html",
  styleUrl: "./entity-performance-ranking.scss",
})
export class EntityPerformanceRankingComponent {
  entities = input.required<EntityPerformanceModelType[]>();
  average = input<number | undefined>(undefined);
  topCount = input<number>(10);

  showRest = signal<boolean>(false);

  private _scaleMax = computed(() => {
    const highest = Math.max(...this.entities().map((e) => e.score ?? 0), this.average() ?? 0);
    return Math.max(highest, 100);
  });

  rankedEntities = computed<RankedEntity[]>(() =>
    this.entities()
      .map((entity) => ({ ...entity, score: entity.score ?? 0 }))
      .sort((a, b) => b.score - a.score)
      .map((entity, index) => ({
        id: entity.entityId,
        rank: index + 1,
        name: AdaaHelper.getItemValueByToken(entity, "entityName"),
        score: entity.score,
        percentage: this._toPercentage(entity.score),
        color: this._entityColor(entity),
        valueText: `${AdaaHelper.roundValue(entity.score)}%`,
      }))
  );

  topEntities = computed(() => this.rankedEntities().slice(0, this.topCount()));
  remainingEntities = computed(() => this.rankedEntities().slice(this.topCount()));

  averagePercentage = computed(() => this._toPercentage(this.average() ?? 0));
  averageText = computed(() => `${AdaaHelper.roundValue(this.average() ?? 0)}%`);
  showAverage = computed(() => AdaaHelper.isDefined(this.average()) && this.entities().length > 0);

  public toggleRest() {
    this.showRest.update((shown) => !shown);
  }

  private _toPercentage(score: number): number {
    return (score / this._scaleMax()) * 100;
  }

  private _entityColor(entity: EntityPerformanceModelType): string {
    if (!AdaaHelper.getItemValueByToken(entity, "entityName")) {
      return Constants.COLOR_CODES[Constants.CONSTANT_COLORS.EVALUATION_GRAY];
    }
    return Constants.COLOR_CODES[entity.scoreColor];
  }
}
